import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { ActivityItem, Channel, Organisation, OrganisationAgent, OrganisationMachine } from '../api/types'
import { type OnOrganisationFrame, useOrganisationStream } from '../live/useOrganisationStream'

export interface PaletteRoom {
  organisation: Organisation
  channel: Channel
}

export interface Rooms {
  organisations: Organisation[]
  /** The organisation on screen. */
  organisation: Organisation | undefined
  /** Its rooms, in the order the rail shows them. */
  channels: Channel[]
  /** The room you are in. */
  channel: Channel | null
  /** Every room across every organisation, which is what ⌘K promises. */
  everywhere: PaletteRoom[]
  activity: ActivityItem[]
  /** Your agents in this organisation, live from its stream, or null until its first agents frame. */
  agents: OrganisationAgent[] | null
  /** Your machines in this organisation, live from its stream, or null until its first machines frame. */
  machines: OrganisationMachine[] | null
  onOrganisationFrame: OnOrganisationFrame
  selectOrganisation: (slug: string) => void
  /** Make one, and go there. Resolves once the rail has it. */
  createOrganisation: (name: string) => Promise<Organisation>
  open: (channel: Channel) => void
  /** Refetch one organisation's rooms, after something changed what they are. */
  reload: (slug: string) => Promise<void>
  /**
   * Put a changed organisation back in the list. A rename moves the rail tile
   * and the room-list header, and both read from here.
   */
  organisationChanged: (organisation: Organisation) => void
  /**
   * One is gone for good. Takes it out of the rail and lands you somewhere that
   * still exists — including nowhere, which is a legitimate place to be now that
   * the rail can make another.
   */
  organisationDeleted: (slug: string) => void
}

/**
 * Where the rail was left. Local to the browser on purpose: it is a convenience,
 * not a preference — the organisation a *new room* lands in is `default_organisation`
 * on the account, and letting an afternoon of reading one organisation quietly
 * decide where tonight's `stagepost init` goes is exactly the confusion this
 * feature must not create.
 */
const LAST_ORGANISATION = 'stagepost.organisation'

function remembered(): string | null {
  try {
    return localStorage.getItem(LAST_ORGANISATION)
  } catch {
    // Private browsing, or storage turned off. Not a reason to fail to load.
    return null
  }
}

function remember(slug: string): void {
  try {
    localStorage.setItem(LAST_ORGANISATION, slug)
  } catch {
    // As above: forgetting is the failure mode, and it is a survivable one.
  }
}

function forget(): void {
  try {
    localStorage.removeItem(LAST_ORGANISATION)
  } catch {
    // Nothing to do about it, and nothing depends on it: the loader falls back
    // when the remembered slug is not in the list anyway.
  }
}

/**
 * Which rooms exist, and which one you are in.
 *
 * Held per organisation rather than for the current one alone: ⌘K goes to any
 * room in any organisation, and it cannot do that from a list scoped to the one
 * on screen. Organisations are few — the ones you are a member of — so this is
 * a handful of requests at start, paid once.
 */
export function useRooms(): Rooms {
  const [organisations, setOrganisations] = useState<Organisation[]>([])
  const [currentOrganisation, setCurrentOrganisation] = useState<string | null>(null)
  const [byOrganisation, setByOrganisation] = useState<Record<string, Channel[]>>({})
  /**
   * The open room, held by id rather than by object.
   *
   * The list is refetched on a link change and rebuilt whenever the
   * organisation stream moves a count, and each makes new objects. Keying the
   * open room on one of those made "the list changed" indistinguishable from
   * "the room changed", so
   * opening a room reloaded it, which cleared the badge, which replaced the
   * list, which reloaded the room.
   */
  const [currentChannelId, setCurrentChannelId] = useState<string | null>(null)
  const [activity, setActivity] = useState<ActivityItem[]>([])

  useEffect(() => {
    void api.organisations().then(async (found) => {
      setOrganisations(found)
      // The one you were last in, unless it is gone — left, or deleted. A rail
      // pointing at something that no longer exists shows nothing at all.
      const last = remembered()
      const landing = found.some((org) => org.slug === last) ? last : found[0]?.slug
      setCurrentOrganisation((previous) => previous ?? landing ?? null)
      const lists = await Promise.all(
        found.map(async (org) => [org.slug, await api.channels(org.slug)] as const))
      setByOrganisation(Object.fromEntries(lists))
    })
  }, [])

  useEffect(() => {
    if (!currentOrganisation) return
    void api.activity(currentOrganisation).then(setActivity)
  }, [currentOrganisation])

  const live = useOrganisationStream(currentOrganisation)

  // The stream's newest counts and blockers over the fetched list, whichever
  // of the two arrived first.
  const channels = useMemo(() => {
    const listed = currentOrganisation ? byOrganisation[currentOrganisation] ?? [] : []
    const byId = new Map(live.rooms?.map((room) => [room.channel_id, room]))
    return listed.map((row) => {
      const room = byId.get(row.id)
      return room
        ? {
            ...row, unread: room.unread, blocked_since: room.blocked_since, last_message_at: room.last_message_at,
            newest_unread: room.newest_unread,
          }
        : row
    })
  }, [byOrganisation, currentOrganisation, live.rooms])

  // Land somewhere when the list arrives, and stay put when it is refetched.
  useEffect(() => {
    setCurrentChannelId((previous) =>
      previous && channels.some((channel) => channel.id === previous)
        ? previous
        : channels[0]?.id ?? null)
  }, [channels])

  const reload = useCallback(async (slug: string) => {
    const found = await api.channels(slug)
    setByOrganisation((previous) => ({ ...previous, [slug]: found }))
  }, [])

  const organisationChanged = useCallback((changed: Organisation) => {
    setOrganisations((previous) =>
      previous.map((org) => (org.slug === changed.slug ? changed : org)))
  }, [])

  const organisationDeleted = useCallback((slug: string) => {
    const left = organisations.filter((org) => org.slug !== slug)
    setOrganisations(left)
    setByOrganisation((previous) => {
      const { [slug]: _gone, ...rest } = previous
      return rest
    })
    if (currentOrganisation !== slug) return
    // Nowhere left is a real answer now: the rail can make another, and the
    // room area says so.
    const next = left[0]?.slug ?? null
    if (next) remember(next)
    else forget()
    setCurrentOrganisation(next)
  }, [currentOrganisation, organisations])

  const selectOrganisation = useCallback((slug: string) => {
    remember(slug)
    setCurrentOrganisation(slug)
  }, [])

  const createOrganisation = useCallback(async (name: string) => {
    const made = await api.createOrganisation(name)
    setOrganisations((previous) => [...previous, made])
    // Seeded explicitly rather than left to fall through to an empty list: a
    // missing entry and an empty one read the same here, and only one of them
    // is a fact.
    setByOrganisation((previous) => ({ ...previous, [made.slug]: [] }))
    remember(made.slug)
    setCurrentOrganisation(made.slug)
    return made
  }, [])

  const open = useCallback((channel: Channel) => {
    // A room reached through the palette may live in another organisation.
    // Setting the organisation alone would let the effect above reset us to
    // that organisation's first room instead.
    setCurrentOrganisation(channel.organisation_slug)
    setCurrentChannelId(channel.id)
    // And it counts as leaving the rail there, which is all this value means.
    // It used to be written only by the rail's own tiles, so crossing to
    // another organisation through the palette and reloading put you back
    // where you had been before — barely noticeable on a desktop, where the
    // palette is one way in among several, and plainly wrong on a phone, where
    // it is the only one.
    remember(channel.organisation_slug)
  }, [])

  return {
    organisations,
    organisation: organisations.find((org) => org.slug === currentOrganisation),
    channels,
    channel: channels.find((channel) => channel.id === currentChannelId) ?? null,
    everywhere: useMemo(
      () => organisations.flatMap((organisation) =>
        (byOrganisation[organisation.slug] ?? []).map((channel) => ({ organisation, channel }))),
      [byOrganisation, organisations],
    ),
    activity,
    agents: live.agents,
    machines: live.machines,
    onOrganisationFrame: live.onFrame,
    selectOrganisation,
    createOrganisation,
    open,
    reload,
    organisationChanged,
    organisationDeleted,
  }
}
